import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { loadStripe } from "@stripe/stripe-js";
import { CheckoutElementsProvider, useCheckoutElements, PaymentElement, BillingAddressElement } from "@stripe/react-stripe-js/checkout";
import { api, errMsg } from "@/lib/api";
import { Spinner } from "@/components/RequireAuth";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useBookingsOpen, ComingSoon } from "@/components/ComingSoon";

const appearance = { theme: "stripe", variables: { colorPrimary: "#0f766e", borderRadius: "12px", fontFamily: "'DM Sans', sans-serif" } };
const fonts = [{ cssSrc: "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&display=swap" }];

function HostedFallback({ bookingId }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${bookingId}/checkout`, { origin_url: window.location.origin, ui: "hosted" });
      window.location.assign(data.checkout_url);
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  return (
    <button type="button" onClick={go} disabled={busy} className="text-sm font-medium text-primary hover:underline" data-testid="pay-hosted-fallback">
      {busy ? "Opening Stripe…" : "Prefer Stripe's secure checkout page? Pay there instead"}
    </button>
  );
}

function Summary({ checkout }) {
  return (
    <div className="space-y-2 rounded-2xl bg-stone-50 p-5 text-sm" data-testid="pay-summary">
      {checkout.lineItems.map((li) => (
        <div key={li.id} className="flex justify-between gap-4"><span>{li.name}</span><span>{li.total.amount}</span></div>
      ))}
      {checkout.total.taxExclusive?.minorUnitsAmount > 0 && (
        <div className="flex justify-between text-stone-600"><span>Taxes</span><span>{checkout.total.taxExclusive.amount}</span></div>
      )}
      <div className="flex justify-between text-stone-600"><span>FairStay service fee</span><span>$0.00</span></div>
      <div className="flex justify-between border-t pt-2 text-base font-semibold"><span>Total</span><span data-testid="pay-total">{checkout.total.total.amount}</span></div>
      <p className="text-xs text-stone-500">Any tax is calculated from your billing address.</p>
    </div>
  );
}

function PayForm() {
  const result = useCheckoutElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  if (result.type === "loading") return <p className="text-sm text-stone-500">Loading secure payment form…</p>;
  if (result.type === "error") return <p className="text-sm text-red-700" data-testid="pay-error">{result.error.message}</p>;
  const { checkout } = result;
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    // On success Stripe redirects to the session's return_url (/payment/success), which confirms the booking.
    const res = await checkout.confirm();
    if (res.type === "error") setError(res.error.message);
    setSubmitting(false);
  };
  return (
    <form onSubmit={submit} className="space-y-6" data-testid="pay-form">
      <Summary checkout={checkout} />
      <BillingAddressElement />
      <PaymentElement />
      {error && <p className="text-sm text-red-700" data-testid="pay-error">{error}</p>}
      <Button type="submit" disabled={submitting} className="h-12 w-full rounded-full text-base" data-testid="pay-submit-button">
        <Lock className="mr-2 h-4 w-4" />{submitting ? "Processing…" : `Pay ${checkout.total.total.amount}`}
      </Button>
    </form>
  );
}

export default function PayBooking() {
  const { bookingId } = useParams();
  const { user } = useAuth();
  const { data: open, isLoading: settingsLoading } = useBookingsOpen();
  const closed = open === false && !user?.is_admin;
  const { data, error, isLoading } = useQuery({
    queryKey: ["pay-session", bookingId],
    queryFn: () => api.post(`/bookings/${bookingId}/checkout`, { origin_url: window.location.origin, ui: "elements" }).then((r) => r.data),
    staleTime: Infinity, retry: false, refetchOnWindowFocus: false, enabled: !settingsLoading && !closed,
  });
  const stripePromise = useMemo(() => (data ? loadStripe(data.publishable_key, data.stripe_account ? { stripeAccount: data.stripe_account } : undefined) : null), [data]);
  if (closed) {
    return (
      <div className="mx-auto max-w-xl px-4 py-10 sm:px-6" data-testid="pay-booking-page">
        <Link to="/trips" className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-primary" data-testid="pay-back-link"><ArrowLeft className="h-4 w-4" />Back to trips</Link>
        <ComingSoon testid="pay-coming-soon" />
      </div>
    );
  }
  return (
    <div className="mx-auto max-w-xl px-4 py-10 sm:px-6" data-testid="pay-booking-page">
      <Link to="/trips" className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-primary" data-testid="pay-back-link"><ArrowLeft className="h-4 w-4" />Back to trips</Link>
      <h1 className="mb-6 mt-3 text-4xl font-bold tracking-tight">Pay for your stay</h1>
      <div className="space-y-6 rounded-3xl border bg-white p-6 sm:p-8">
        {!error && <div className="text-center"><HostedFallback bookingId={bookingId} /></div>}
        {isLoading && <Spinner />}
        {error && <p className="text-sm text-red-700" data-testid="pay-error">{errMsg(error)}</p>}
        {data && (
          <CheckoutElementsProvider stripe={stripePromise} options={{ clientSecret: data.client_secret, elementsOptions: { appearance, fonts } }}>
            <PayForm />
          </CheckoutElementsProvider>
        )}
      </div>
      <p className="mt-4 flex items-center justify-center gap-1 text-xs text-stone-500"><Lock className="h-3 w-3" />Card details go straight to Stripe. FairStay never sees your full card number.</p>
    </div>
  );
}
